/**
 * Facebook ads and page: response shapes (routes/facebook.js) and the small
 * rules the pages share. No imports and only erasable types, so node --test
 * loads this file directly (test/facebook.test.js).
 */
export type Period = { from: string; to: string; days: number; previous: { from: string; to: string } };
export type FbLink = {
  id: number; account_id: string; account_name: string; writes_enabled: boolean; current_credentials: boolean | null;
  last_synced_at: string | null; last_attempt_at: string | null; last_status: "success" | "empty" | "failed" | null; last_error: string | null;
};
export type Totals = {
  spend: number; impressions: number; clicks: number; link_clicks: number | null; leads: number | null; messages: number | null; calls: number | null;
  ctr: number | null; cpc: number | null; cpm: number | null; cpl: number | null;
};
export type FbCampaign = Totals & {
  id: string; name: string; status: string | null; configured_status: string | null; objective: string | null;
  daily_budget: number | null; lifetime_budget: number | null; known: boolean;
};
export type FbObject = {
  level: "adset" | "ad"; object_id: string; campaign_id: string | null; adset_id: string | null; name: string | null;
  status: string | null; configured_status: string | null; daily_budget: number | null; lifetime_budget: number | null;
  thumbnail_url: string | null; permalink: string | null; body: string | null; title: string | null; cta: string | null;
  spend: number | null; impressions: number | null; clicks: number | null; leads: number | null;
};
export type FbChange = {
  id: number; link_id: number; approval_id: number; action: string; params: Record<string, unknown>; status: string; result_message: string | null;
  created_at: string; finished_at: string | null; approval_status: string; title: string | null; by_name: string | null;
};
export type AdsDay = { date: string; spend: number | null; leads: number | null; clicks: number | null };
export type FbAdsReport = {
  links: FbLink[]; link: FbLink | null; period?: Period; totals?: Totals; previous?: Totals | null; daily?: AdsDay[];
  campaigns?: FbCampaign[]; adsets?: FbObject[]; ads?: FbObject[]; changes?: FbChange[];
};
export type PageDay = { date: string; followers: number | null; new_follows: number | null; viewers: number | null; engagements: number | null };
export type FbPost = {
  post_id: string; created_at: string | null; message: string | null; permalink: string | null; picture: string | null;
  viewers: number | null; engagements: number | null; reactions: number | null; comments: number | null; shares: number | null; clicks: number | null; video_views: number | null;
};
export type PageTotals = {
  followers: number | null; followers_change: number | null; new_follows: number | null; unfollows: number | null;
  viewers_avg: number | null; engagements: number | null; page_views: number | null; posts: number;
};
export type FbPageReport = {
  links: FbLink[]; link: FbLink | null; period?: Period; totals?: PageTotals;
  previous?: { new_follows: number | null; viewers_avg: number | null; engagements: number | null; page_views: number | null; posts: number | null } | null;
  daily?: PageDay[]; posts?: FbPost[]; changes?: FbChange[];
};

/** Percent change, or null when there is nothing to compare with (never "+100%" from zero). */
export function pctChange(now: number | null | undefined, before: number | null | undefined): number | null {
  if (now === null || now === undefined || before === null || before === undefined || !Number.isFinite(now) || !Number.isFinite(before) || before === 0) return null;
  return ((now - before) / before) * 100;
}

/** Which way is good news. Spend is neither. */
const GOOD: Record<string, "up" | "down" | "neutral"> = {
  spend: "neutral", leads: "up", cpl: "down", clicks: "up", ctr: "up", cpm: "down", cpc: "down",
  new_follows: "up", viewers_avg: "up", engagements: "up", page_views: "up", posts: "up",
};
export function tone(metric: string, change: number | null): "positive" | "negative" | "neutral" | null {
  if (change === null) return null;
  const good = GOOD[metric] || "neutral";
  if (good === "neutral" || change === 0) return "neutral";
  return (change > 0) === (good === "up") ? "positive" : "negative";
}

/** Meta's statuses in words. A paused parent pauses everything under it. */
export function statusWords(status: string | null | undefined): { label: string; on: boolean } {
  switch (status) {
    case "ACTIVE": return { label: "On", on: true };
    case "PAUSED": return { label: "Paused", on: false };
    case "CAMPAIGN_PAUSED": return { label: "Campaign paused", on: false };
    case "ADSET_PAUSED": return { label: "Ad set paused", on: false };
    case "IN_PROCESS": return { label: "Processing", on: true };
    case "WITH_ISSUES": return { label: "Has issues", on: false };
    case "PENDING_REVIEW": return { label: "In review", on: false };
    case "DISAPPROVED": return { label: "Rejected", on: false };
    case "PREAPPROVED": return { label: "Approved, not started", on: false };
    case "PENDING_BILLING_INFO": return { label: "Needs billing", on: false };
    case "ARCHIVED": return { label: "Archived", on: false };
    case "DELETED": return { label: "Deleted", on: false };
    case null: case undefined: case "": return { label: "Unknown", on: false };
    default: return { label: status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " "), on: false };
  }
}

/** Can this object be switched on or off from here? Only its own switch, not a parent's. */
export function switchable(configured: string | null | undefined): boolean {
  return configured === "ACTIVE" || configured === "PAUSED";
}

/** Meta objectives, as a person would say them. */
export function objectiveWords(objective: string | null | undefined): string | null {
  if (!objective) return null;
  const map: Record<string, string> = {
    OUTCOME_LEADS: "Leads", OUTCOME_TRAFFIC: "Traffic", OUTCOME_AWARENESS: "Awareness", OUTCOME_ENGAGEMENT: "Engagement",
    OUTCOME_SALES: "Sales", OUTCOME_APP_PROMOTION: "App installs", LEAD_GENERATION: "Leads", LINK_CLICKS: "Traffic",
    MESSAGES: "Messages", REACH: "Reach", BRAND_AWARENESS: "Awareness", CONVERSIONS: "Conversions", POST_ENGAGEMENT: "Engagement",
  };
  return map[objective] || objective.charAt(0) + objective.slice(1).toLowerCase().replace(/^outcome_/, "").replace(/_/g, " ");
}

/** A daily budget may move at most 20% at a time (utils/safetyRules.js caps it). Whole cents. */
export function budgetRange(now: number): { min: number; max: number } {
  return { min: Math.ceil(now * 0.8 * 100) / 100, max: Math.floor(Math.min(now * 1.2, 1644) * 100) / 100 };
}

/** The words for a change's state on the Changes list. */
export function changeState(c: Pick<FbChange, "status" | "approval_status">): { label: string; tone: "success" | "warning" | "danger" | "neutral"; runnable: boolean } {
  if (c.status === "verified") return { label: "Done", tone: "success", runnable: false };
  if (c.status === "accepted") return { label: "Sent, not yet showing", tone: "success", runnable: false };
  if (c.status === "unknown" || c.status === "executing") return { label: "Check in Meta", tone: "warning", runnable: false };
  if (c.status === "blocked") return { label: "Blocked", tone: "danger", runnable: false };
  if (c.approval_status === "rejected") return { label: "Rejected", tone: "neutral", runnable: false };
  if (c.approval_status === "approved") return { label: "Approved, ready to run", tone: "warning", runnable: true };
  return { label: "Waiting for approval", tone: "neutral", runnable: false };
}

/** The post's first line, for a table cell. */
export function firstLine(text: string | null | undefined, max = 140): string {
  const t = (text || "").replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/** Windsor returns Page reels in the post feed; the canonical Meta permalink identifies them. */
export function facebookPostFormat(post: Pick<FbPost, "permalink">): "reel" | "post" {
  try {
    const url = new URL(post.permalink || "");
    return /(^|\.)facebook\.com$/i.test(url.hostname) && /^\/reels?\/[^/]+/i.test(url.pathname) ? "reel" : "post";
  } catch { return "post"; }
}
